import {
  isAntonImpostorBannedByTenure,
  isGoldenHandoffBannedByTenure,
  isUnlockedFullTimeBannedByTenure,
  isWarmthWarriorBannedByTenure,
} from 'features/catalog/badge-definitions';
import { Badge } from 'features/catalog/useBadges';
import { isBadgeReleased } from 'features/catalog/badge-release';
import { BadgeModes } from 'types/badge-modes';

export interface GetBadgePropertiesProps {
  badge: Badge;
  mode: BadgeModes;
  enabledBadgesMap: Map<string, boolean>;
  profileBadgeIDs: string[] | null;
  badgeCreditsSubmissions: string[];
  badgeDesignSubmissions: string[];
  submittedBadgeIDSet?: ReadonlySet<string>;
  badgeCreditSubmissionIDSet?: ReadonlySet<string>;
  badgeDesignSubmissionIDSet?: ReadonlySet<string>;
  profileBadgeIDSet?: ReadonlySet<string>;
}

export const getBadgeProperties = ({
  badge,
  mode,
  enabledBadgesMap,
  badgeCreditsSubmissions,
  badgeDesignSubmissions,
  profileBadgeIDs,
  submittedBadgeIDSet,
  badgeCreditSubmissionIDSet,
  badgeDesignSubmissionIDSet,
  profileBadgeIDSet,
}: GetBadgePropertiesProps) => {
  const submittedIDs = submittedBadgeIDSet ?? new Set([...badgeCreditsSubmissions, ...badgeDesignSubmissions]);
  const creditIDs = badgeCreditSubmissionIDSet ?? new Set(badgeCreditsSubmissions);
  const designIDs = badgeDesignSubmissionIDSet ?? new Set(badgeDesignSubmissions);

  const lowOpacity =
    mode === 'submitted'
      ? !submittedIDs.has(badge.id)
      : profileBadgeIDs != null && !enabledBadgesMap.get(badge.id) && mode !== 'badge_gallery';

  const designIncluded =
    mode === 'submitted' && (designIDs.has(badge.id) || (badge.design == null && creditIDs.has(badge.id)));

  const ideaIncluded = mode === 'submitted' && creditIDs.has(badge.id);

  const display = mode === 'submitted' ? submittedIDs.has(badge.id) : true;

  const badgeIsDesignedOrSubmittedByThisPerson = creditIDs.has(badge.id) || designIDs.has(badge.id);

  const blackAndWhite =
    mode === 'badge_gallery'
      ? false
      : mode === 'submitted'
        ? !badgeIsDesignedOrSubmittedByThisPerson
        : !enabledBadgesMap.get(badge.id);

  const disableInteraction =
    (mode === 'submitted' && !badgeIsDesignedOrSubmittedByThisPerson) ||
    (mode === 'editing_profile' &&
      !enabledBadgesMap.get(badge.id) &&
      (!isBadgeReleased(badge.id) ||
        badge.banSelfClaim ||
        (badge.restrictedRules &&
          badge.restrictedRules.ids.filter((id) => enabledBadgesMap.get(id)).length < badge.restrictedRules.count) ||
        (badge.id === 'coi1' && isGoldenHandoffBannedByTenure(enabledBadgesMap)) ||
        (badge.id === 'intr' && isUnlockedFullTimeBannedByTenure(enabledBadgesMap)) ||
        (badge.id === 'xray' && isAntonImpostorBannedByTenure(enabledBadgesMap)) ||
        (badge.id === 'warm' && isWarmthWarriorBannedByTenure(enabledBadgesMap))));

  const showName = !disableInteraction && (mode !== 'submitted' || submittedIDs.has(badge.id));

  const originallyClaimed =
    mode === 'editing_profile' &&
    !!(profileBadgeIDSet?.has(badge.id) ?? profileBadgeIDs?.includes(badge.id)) &&
    !!enabledBadgesMap.get(badge.id);

  const cursorNotAllowed =
    disableInteraction ||
    originallyClaimed ||
    (mode === 'editing_profile' &&
      badge.autoClaimRules &&
      badge.autoClaimRules.ids.some((id) => enabledBadgesMap.get(id)));

  return {
    blackAndWhite,
    display,
    lowOpacity,
    disableInteraction,
    showName,
    designIncluded,
    ideaIncluded,
    cursorNotAllowed,
  };
};
